// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { forwardRef, Ref } from 'react';
import clsx from 'clsx';

import { isThemeActive, Theme } from '@amzn/awsui-component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@amzn/awsui-component-toolkit/internal/analytics-metadata';

import InternalIcon from '../icon/internal';
import { fireNonCancelableEvent, NonCancelableEventHandler } from '../internal/events';
import { GeneratedAnalyticsMetadataTokenDismiss } from './analytics-metadata/interfaces';

import legacyTestingStyles from '../token-group/styles.css.js';
import styles from './styles.css.js';
import testUtilStyles from './test-classes/styles.css.js';

interface DismissButtonProps {
  disabled?: boolean;
  readOnly?: boolean;
  onDismiss?: NonCancelableEventHandler;
  dismissLabel?: string;
  inline?: boolean;
}

export default forwardRef(DismissButton);

function DismissButton(
  { disabled, dismissLabel, onDismiss, readOnly, inline }: DismissButtonProps,
  ref: Ref<HTMLButtonElement>
) {
  const isOneTheme = isThemeActive(Theme.OneTheme);
  const analyticsMetadata: GeneratedAnalyticsMetadataTokenDismiss = {
    action: 'dismiss',
    detail: {
      label: { root: 'self' },
    },
  };
  return (
    <button
      ref={ref}
      type="button"
      className={clsx(
        styles['dismiss-button'],
        legacyTestingStyles['dismiss-button'],
        testUtilStyles['dismiss-button'],
        inline && styles['dismiss-button-inline']
      )}
      aria-disabled={disabled || readOnly ? true : undefined}
      onClick={() => {
        if (disabled || readOnly || !onDismiss) {
          return;
        }

        fireNonCancelableEvent(onDismiss);
      }}
      aria-label={dismissLabel}
      {...(disabled || readOnly ? {} : getAnalyticsMetadataAttribute(analyticsMetadata))}
    >
      <InternalIcon name="close" size={isOneTheme ? 'x-small' : undefined} />
    </button>
  );
}
